import { Component, Input, Output, EventEmitter } from '@angular/core';
import { PoPermissionsConfigService } from '../../services/po-permissions-config.service';
import { HasPermissionsService } from '../../../shared/services/has-permissions/has-permissions.service';
import {
  PO_ACTION,
} from '../purchase-order-detail/purchase-order.constants';

/**
 * Approval action buttons for the purchase order view.
 *
 * Visibility is driven by permissions.config.json — each button is shown only if
 * the config allows the action for the current source_type + stage AND the user
 * has the corresponding permission role.
 *
 * The parent component handles the actual API calls; this component only decides
 * which buttons to show and emits the corresponding event on click.
 */
@Component({
  selector: 'app-po-approval-actions',
  templateUrl: './po-approval-actions.component.html',
  styleUrls: ['./po-approval-actions.component.scss'],
})
export class PoApprovalActionsComponent {
  @Input() statusCode: string | null = null;
  @Input() sourceTypeCode = '';

  @Output() resubmitForApproval = new EventEmitter<void>();
  @Output() retrySettlement = new EventEmitter<void>();
  @Output() retryTrato = new EventEmitter<void>();
  @Output() approvePurchaseOrder = new EventEmitter<void>();
  @Output() returnToKam = new EventEmitter<void>();

  constructor(
    private readonly permissionsConfig: PoPermissionsConfigService,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  get canResubmit(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.RESUBMIT_FOR_APPROVAL);
  }

  get canRetrySettlement(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.RETRY_SETTLEMENT);
  }

  get canRetryTrato(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.RETRY_TRATO);
  }

  get canApprove(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.APPROVE_PO);
  }

  // Admins see the approve button but cannot click it.
  get isApproveDisabled(): boolean {
    return this.permissionsService.isAdmin();
  }

  get canReturnToKam(): boolean {
    return this.permissionsConfig.canPerformAction(this.sourceTypeCode, this.statusCode, PO_ACTION.RETURN_TO_KAM);
  }

  get hasAnyAction(): boolean {
    return this.canResubmit
      || this.canRetrySettlement
      || this.canRetryTrato
      || this.canApprove
      || this.canReturnToKam;
  }
}
